import React, { useCallback, useMemo } from "react";
import { MdAddShoppingCart } from "react-icons/md";
import { FormattedMessage, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Icon } from "semantic-ui-react";
import { addToList } from "../../../../../actions/cartActions";
import { setAlert } from "../../../../../actions/alertActions";
import { cartSelector } from "../../../../../utils/selectors";
import MainButton from "../../../../../components/Button/MainButton";
import { CartData } from "../../../../../types/siteTypes";
import {
  formatPatternSizeLabel,
  getOrderedPatternSizes,
  resolveCartSelectedSize,
} from "../../../../../utils/patternSize";
import styles from "./CartButton.module.scss";

type Props = {
  id: string;
  sizes?: string[];
  selectedSizes?: string[];
  onSelectedSizesChange?: (sizes: string[]) => void;
};

const CartButton: React.FC<Props> = ({
  id,
  sizes = [],
  selectedSizes = [],
  onSelectedSizesChange,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const intl = useIntl();
  const cart = useSelector((state: any) => cartSelector(state));
  const inCart = cart.filter((item: CartData) => item.id === id).length > 0;
  const orderedAvailableSizes = useMemo(
    () => getOrderedPatternSizes(sizes),
    [sizes]
  );
  const selectedCartSizesForPattern = useMemo(
    () =>
      cart
        .filter((item: CartData) => item.id === id)
        .map((item: CartData) => resolveCartSelectedSize(item))
        .filter(Boolean),
    [cart, id]
  );
  const selectableSizes = useMemo(
    () =>
      orderedAvailableSizes.filter(
        (size) => !selectedCartSizesForPattern.includes(size)
      ),
    [orderedAvailableSizes, selectedCartSizesForPattern]
  );
  const selectedSizesToAdd = useMemo(
    () =>
      (Array.isArray(selectedSizes) ? selectedSizes : []).filter((size) =>
        selectableSizes.includes(size)
      ),
    [selectedSizes, selectableSizes]
  );
  const hasMultipleSizes = orderedAvailableSizes.length > 1;
  const showGoToCart = !hasMultipleSizes && inCart;

  const addPatternToCart = useCallback((selectedSizes?: string[]) => {
    const sizesToAdd =
      selectedSizes && selectedSizes.length > 0
        ? selectedSizes
        : orderedAvailableSizes.length === 1
          ? [orderedAvailableSizes[0]]
          : [];

    if (sizesToAdd.length === 0) {
      return;
    }

    dispatch(
      addToList(
        id,
        "cart",
        sizesToAdd.length === 1 ? sizesToAdd[0] : sizesToAdd
      )
    );

    if (sizesToAdd.length === 1) {
      dispatch(
        setAlert(
          intl.formatMessage(
            {
              id: "cart.addedToCart.single",
              defaultMessage: "Added to cart: {size}",
            },
            {
              size: formatPatternSizeLabel(sizesToAdd[0]),
            }
          ),
          "success"
        )
      );
      return;
    }

    dispatch(
      setAlert(
        intl.formatMessage(
          {
            id: "cart.addedToCart.multiple",
            defaultMessage: "Added sizes to cart: {sizes}",
          },
          {
            sizes: sizesToAdd
              .map((size) => formatPatternSizeLabel(size))
              .join(", "),
          }
        ),
        "success"
      )
    );
  }, [dispatch, id, orderedAvailableSizes, intl]);

  return (
    <MainButton
      className={styles.self}
      onClick={() => {
        if (hasMultipleSizes) {
          if (selectableSizes.length === 0) {
            dispatch(
              setAlert(
                intl.formatMessage({
                  id: "cart.allSizesAlreadyInCart",
                  defaultMessage: "All sizes of this pattern are already in cart",
                }),
                "info"
              )
            );
            navigate("/cart");
            return;
          }

          if (selectedSizesToAdd.length === 0) {
            dispatch(
              setAlert(
                intl.formatMessage({
                  id: "cart.selectAtLeastOneSize",
                  defaultMessage: "Please select at least one size",
                }),
                "info"
              )
            );
            return;
          }

          addPatternToCart(selectedSizesToAdd);
          onSelectedSizesChange?.([]);
          return;
        }

        if (inCart) {
          navigate("/cart");
        } else {
          addPatternToCart();
        }
      }}
    >
      {showGoToCart ? (
        <>
          <FormattedMessage
            id={"cart.goToyourCart"}
            defaultMessage={"Go to your cart"}
          />{" "}
          <Icon className={styles.icon} name={"chevron right"} />
        </>
      ) : (
        <>
          <FormattedMessage
            id={"cart.addToCart"}
            defaultMessage={"Add to Cart"}
          />{" "}
          <MdAddShoppingCart className={styles.icon} size={24} />
        </>
      )}
    </MainButton>
  );
};

export default CartButton;
